import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../contexts/AppContext";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { validateCoupon } from "../services/analytics";
import { validateGiftCardCode } from "../services/giftCards";
import { getEpointConfig } from "../services/firestore";
import { startEpointCheckout } from "../services/epoint";
import { Truck, Zap, Store, Mail, Lock, Ticket, Gift, CreditCard, UserPlus } from "lucide-react";
import { COUNTRIES, DEFAULT_COUNTRY } from "../lib/countries";

const iconMap = { truck: Truck, zap: Zap, store: Store, mail: Mail };

export default function CheckoutPage() {
  const { t, formatPrice, currency } = useApp();
  const { user, profile, authChecked } = useAuth();
  const { items, subtotalAzn } = useCart();
  const nav = useNavigate();
  const [methods, setMethods] = useState([]);
  const [delivery, setDelivery] = useState(null);
  const [form, setForm] = useState({
    full_name: "", phone: "", address: "", city: "", country: "AZ", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponErr, setCouponErr] = useState("");
  const [epointEnabled, setEpointEnabled] = useState(false);

  // Cart contains only gift-card items?
  const onlyGiftCards = items.length > 0 && items.every((i) => i.is_gift_card);
  const hasGiftCards = items.some((i) => i.is_gift_card);

  useEffect(() => {
    api.get("/delivery-methods").then((r) => {
      const list = r.data || [];
      setMethods(list);
      if (list[0]) setDelivery(list[0]);
    });
    getEpointConfig()
      .then((cfg) => setEpointEnabled(!!(cfg && cfg.enabled && cfg.public_key && cfg.private_key)))
      .catch(() => setEpointEnabled(false));
  }, []);

  // For gift-card-only carts no physical delivery is required.
  const effectiveDeliveryPrice = onlyGiftCards ? 0 : (delivery?.price_azn || 0);

  // Compute applicable discount based on coupon OR gift-card code.
  let discountAzn = 0;
  if (coupon) {
    if (coupon.is_gift_card) {
      discountAzn = Math.min(Number(coupon.balance_azn) || 0, subtotalAzn);
    } else {
      discountAzn = coupon.discount_percent > 0
        ? subtotalAzn * (Number(coupon.discount_percent) / 100)
        : (Number(coupon.discount_amount) || 0);
      discountAzn = Math.min(discountAzn, subtotalAzn);
    }
  }
  const total = Math.max(0, subtotalAzn - discountAzn) + effectiveDeliveryPrice;

  const applyCoupon = async () => {
    setCouponErr("");
    const code = (couponInput || "").trim();
    if (!code) return;

    // Don't allow redeeming a gift card to pay for itself (only).
    const c = await validateCoupon(code, user?.uid);
    if (c) {
      setCoupon({ ...c, is_gift_card: false });
      return;
    }
    const gc = await validateGiftCardCode(code);
    if (gc) {
      if (onlyGiftCards) {
        setCouponErr("Hədiyyə kartı yalnız adi məhsullara tətbiq oluna bilər.");
        setCoupon(null);
        return;
      }
      setCoupon({
        id: gc.id,
        code: gc.code,
        is_gift_card: true,
        balance_azn: Number(gc.balance_azn) || 0,
        multi_use: !!gc.multi_use,
      });
      return;
    }
    setCouponErr(t.coupon.invalid);
    setCoupon(null);
  };

  const removeCoupon = () => { setCoupon(null); setCouponInput(""); setCouponErr(""); };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const placeOrder = async () => {
    if (!user) {
      nav("/login?next=/checkout", { replace: false });
      return;
    }
    if (items.length === 0) return alert(t.checkout.cartEmptyAlert);
    if (!epointEnabled) {
      return alert("Ödəniş sistemi hazırda əlçatan deyil. Zəhmət olmasa bir az sonra yenidən cəhd edin.");
    }
    if (!onlyGiftCards && !delivery) return alert(t.checkout.fillAllAlert);
    if (!form.full_name || !form.phone || (!onlyGiftCards && !form.address)) {
      return alert(t.checkout.fillAllAlert);
    }
    setSubmitting(true);
    try {
      const orderItems = items.map((i) => i.is_gift_card ? ({
        product_id: i.product_id, title: i.title, artist_name: "",
        cover_url: i.cover_url, price_azn: i.price_azn, qty: i.qty,
        is_gift_card: true,
        gift_card_template_id: i.gift_card_template_id,
        gift_card_amount_azn: i.gift_card_amount_azn,
        multi_use: i.multi_use,
        recipient_name: i.recipient_name || "",
        recipient_email: i.recipient_email || "",
        gift_message: i.gift_message || "",
      }) : ({
        product_id: i.product_id, title: i.title, artist_name: i.artist_name,
        cover_url: i.cover_url, price_azn: i.price_azn, qty: i.qty,
      }));

      const payload = {
        items: orderItems,
        delivery_code: onlyGiftCards ? "digital" : delivery.code,
        delivery_price_azn: effectiveDeliveryPrice,
        full_name: form.full_name, phone: form.phone,
        address: form.address, city: form.city,
        country: (form.country || DEFAULT_COUNTRY).toUpperCase(),
        notes: form.notes,
        currency,
        user_id: user?.uid || null,
        coupon_code: coupon?.code || null,
        discount_azn: discountAzn,
        // Order starts in awaiting_payment — flipped to "placed" only after
        // Epoint confirms the charge on the /success landing page.
        initial_status: "awaiting_payment",
      };
      const { data } = await api.post("/orders", payload);

      // ─── EPOINT FLOW (only payment option) ──────────────────────────
      // Park the post-payment side-effects (gift card issuance, coupon
      // redemption) in localStorage so the /success landing page can apply
      // them once the bank confirms the charge.
      const pending = {
        order_doc_id: data.id,
        tracking_no: data.tracking_no,
        total_azn: total,
        gift_card_items: items
          .filter((i) => i.is_gift_card)
          .map((it) => ({
            template_id: it.gift_card_template_id || null,
            name: (it.title || "").replace(/ — Hədiyyə Kartı$/, ""),
            amount_azn: it.gift_card_amount_azn || it.price_azn,
            multi_use: it.multi_use !== false,
            purchaser_user_id: user?.uid || null,
            purchaser_email: profile?.email || user?.email || null,
            recipient_name: it.recipient_name || "",
            recipient_email: it.recipient_email || "",
            gift_message: it.gift_message || "",
          })),
        coupon: coupon
          ? {
              id: coupon.id,
              code: coupon.code,
              is_gift_card: !!coupon.is_gift_card,
              amount_azn: discountAzn,
            }
          : null,
      };
      try {
        localStorage.setItem(`epoint_pending_${data.tracking_no}`, JSON.stringify(pending));
      } catch { /* quota */ }

      const description = `Vinyl Baku — ${data.tracking_no}`;
      await startEpointCheckout({
        trackingNo: data.tracking_no,
        amountAzn: total,
        description,
      });
      // startEpointCheckout navigates away — control should not return.
    } catch (e) {
      console.error(e);
      alert((e && e.message) || t.checkout.placeFailedAlert);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="checkout-page">
      {/* Guest gate — must be logged in to place an order */}
      {authChecked && !user && (
        <div className="max-w-xl mx-auto text-center py-12" data-testid="checkout-guest-gate">
          <div className="w-16 h-16 mx-auto rounded-full bg-[var(--surface)] border border-[var(--border)] flex items-center justify-center text-[var(--accent,#C8332C)] mb-5">
            <UserPlus size={26} />
          </div>
          <h1 className="font-display text-3xl md:text-4xl mb-3">Sifariş üçün hesab tələb olunur</h1>
          <p className="text-[var(--text-2)] text-sm mb-8 max-w-md mx-auto leading-relaxed">
            Çatdırılma, ödəniş və zəmanət üçün davam etmədən əvvəl hesabınıza daxil olun və ya
            qısa qeydiyyatdan keçin.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/login?next=/checkout" className="btn-primary" data-testid="checkout-login-btn">Daxil ol</Link>
            <Link to="/register?next=/checkout" className="btn-outline" data-testid="checkout-register-btn">Qeydiyyat</Link>
          </div>
        </div>
      )}

      {authChecked && user && (<>
      <div className="metadata">{t.checkout.trackingPending}</div>
      <h1 className="section-title text-5xl md:text-7xl mt-3">{t.checkout.title}</h1>
      <p className="text-[var(--text-2)] mt-4 max-w-2xl">{t.checkout.subtitle}</p>

      {hasGiftCards && (
        <div className="mt-6 border border-[var(--primary)] bg-[var(--surface)] p-4 flex items-start gap-3 text-sm" data-testid="gift-card-notice">
          <Gift size={18} className="text-[var(--primary)] mt-0.5" />
          <div>
            <div className="font-display text-lg">Səbətinizdə hədiyyə kartı var</div>
            <div className="text-[var(--text-2)]">Ödəniş tamamlandıqdan sonra hər kart üçün unikal endirim kodu yaradılacaq və profilinizdə ("Hədiyyə Kartlarım" bölməsində) görünəcək.</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-12 gap-10 mt-14">
        <div className="md:col-span-7 space-y-12">
          {/* Address */}
          <section>
            <div className="metadata mb-4">{t.checkout.shippingDetails}</div>
            <div className="grid grid-cols-2 gap-4">
              <input className="input-base col-span-2" placeholder={t.checkout.fullName} value={form.full_name} onChange={onChange("full_name")} data-testid="form-fullname" />
              <input className="input-base" placeholder={t.checkout.phone} value={form.phone} onChange={onChange("phone")} data-testid="form-phone" />
              <input className="input-base" placeholder={t.checkout.city} value={form.city} onChange={onChange("city")} data-testid="form-city" />
              <div className="col-span-2">
                <label className="metadata block mb-1.5 text-[var(--text-muted)]">Ölkə</label>
                <select
                  className="input-base w-full"
                  value={form.country || DEFAULT_COUNTRY}
                  onChange={onChange("country")}
                  data-testid="form-country"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.flag}  {c.name}</option>
                  ))}
                </select>
              </div>
              <input className={`input-base col-span-2 ${onlyGiftCards ? "opacity-60" : ""}`} placeholder={onlyGiftCards ? `${t.checkout.address} (opsional)` : t.checkout.address} value={form.address} onChange={onChange("address")} data-testid="form-address" />
              <textarea className="input-base col-span-2" rows={2} placeholder={t.checkout.notes} value={form.notes} onChange={onChange("notes")} data-testid="form-notes" />
            </div>
          </section>

          {/* Shipping */}
          {!onlyGiftCards && (
            <section>
              <div className="metadata mb-4">{t.checkout.shippingMethod}</div>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="delivery-methods">
                {methods.map((m) => {
                  const Icon = iconMap[m.icon] || Truck;
                  const active = delivery?.id === m.id;
                  return (
                    <button
                      key={m.id} type="button" onClick={() => setDelivery(m)}
                      className={`text-left p-5 border ${active ? "border-[var(--primary)] bg-[var(--surface)]" : "border-[var(--border)]"}`}
                      data-testid={`delivery-${m.code}`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3"><Icon size={18} /><span className="metadata">{m.duration}</span></div>
                        <span className="font-mono text-sm">{m.price_azn === 0 ? t.common.free : formatPrice(m.price_azn)}</span>
                      </div>
                      <div className="font-display text-2xl mt-3">{m.name}</div>
                      <div className="text-[var(--text-2)] text-xs mt-1">{m.description}</div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Payment */}
          <section>
            <div className="metadata mb-4 flex items-center gap-2"><Lock size={12} /> {t.checkout.payment}</div>
            {epointEnabled ? (
              <div className="border border-[var(--primary)] bg-[var(--surface)] p-5 flex items-start gap-4" data-testid="epoint-payment-card">
                <div className="w-11 h-11 rounded-full bg-[var(--bg)] border border-[var(--primary)] flex items-center justify-center text-[var(--primary)] shrink-0">
                  <CreditCard size={20} />
                </div>
                <div className="flex-1">
                  <div className="font-display text-xl">Kart ilə təhlükəsiz ödəniş</div>
                  <p className="text-sm text-[var(--text-2)] mt-1 leading-relaxed">
                    Sifarişi təsdiqlədikdən sonra Epoint.az təhlükəsiz ödəniş səhifəsinə yönləndiriləcəksiniz.
                    Ödəniş AZN valyutasında qəbul olunur. Visa / MasterCard.
                  </p>
                </div>
              </div>
            ) : (
              <div className="border border-[var(--accent)] bg-[var(--surface)] p-5 flex items-start gap-4" data-testid="epoint-disabled-card">
                <div className="w-11 h-11 rounded-full bg-[var(--bg)] border border-[var(--accent)] flex items-center justify-center text-[var(--accent)] shrink-0">
                  <Lock size={20} />
                </div>
                <div className="flex-1">
                  <div className="font-display text-xl">Ödəniş sistemi hazırda əlçatan deyil</div>
                  <p className="text-sm text-[var(--text-2)] mt-1 leading-relaxed">
                    Epoint ödəniş şlüzü konfiqurasiya olunmayıb. Zəhmət olmasa bir az sonra yenidən cəhd edin
                    və ya bizimlə əlaqə saxlayın.
                  </p>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Summary */}
        <aside className="md:col-span-5">
          <div className="border border-[var(--border)] p-6 sticky top-32">
            <div className="metadata mb-4">{t.checkout.orderSummary}</div>
            <ul className="space-y-3">
              {items.map((i) => (
                <li key={i.product_id} className="flex gap-3 text-sm">
                  {i.is_gift_card && !i.cover_url ? (
                    <div className="w-12 h-12 border border-[var(--border-soft)] bg-[var(--surface)] flex items-center justify-center text-[var(--primary)]"><Gift size={18} /></div>
                  ) : (
                    <img src={i.cover_url} className="w-12 h-12 object-cover" alt="" />
                  )}
                  <div className="flex-1">
                    <div className="font-display text-base leading-tight flex items-center gap-2">
                      {i.is_gift_card && <Gift size={12} className="text-[var(--primary)]" />}
                      {i.title}
                    </div>
                    <div className="metadata">×{i.qty}{i.is_gift_card && i.recipient_name ? ` · → ${i.recipient_name}` : ""}</div>
                  </div>
                  <div className="font-mono">{formatPrice(i.price_azn * i.qty)}</div>
                </li>
              ))}
            </ul>
            <div className="border-t border-[var(--border-soft)] mt-5 pt-5 space-y-2 font-mono text-sm">
              <div className="flex justify-between"><span className="metadata">{t.common.subtotal}</span><span>{formatPrice(subtotalAzn)}</span></div>
              {coupon && (
                <div className="flex justify-between text-[var(--accent)]">
                  <span className="metadata flex items-center gap-1">
                    {coupon.is_gift_card ? <Gift size={11} /> : null}
                    {coupon.is_gift_card ? "HƏDİYYƏ KARTI" : t.coupon.discount} ({coupon.code})
                  </span>
                  <span>− {formatPrice(discountAzn)}</span>
                </div>
              )}
              <div className="flex justify-between"><span className="metadata">{t.common.delivery}</span><span>{effectiveDeliveryPrice === 0 ? t.common.free : formatPrice(effectiveDeliveryPrice)}</span></div>
              <div className="flex justify-between text-2xl font-display mt-3"><span>{t.common.total}</span><span data-testid="checkout-total">{formatPrice(total)}</span></div>
            </div>

            {/* Coupon / Gift card code */}
            <div className="mt-5 pt-5 border-t border-[var(--border-soft)]">
              <div className="metadata mb-2 flex items-center gap-2"><Ticket size={12} /> {t.coupon.label} / HƏDİYYƏ KARTI</div>
              {coupon ? (
                <div className="flex items-center justify-between text-sm">
                  <span className="font-mono text-[var(--primary)] flex items-center gap-1">
                    {coupon.is_gift_card && <Gift size={12} />}
                    {coupon.code}
                  </span>
                  <button onClick={removeCoupon} className="metadata text-[var(--accent)] link-underline" data-testid="coupon-remove">{t.coupon.remove}</button>
                </div>
              ) : (
                <div className="flex gap-0">
                  <input value={couponInput} onChange={(e) => setCouponInput(e.target.value)} placeholder="WELCOME10 / GC-XXXX-XXXX-XXXX" className="flex-1 bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs uppercase" data-testid="coupon-input" />
                  <button onClick={applyCoupon} className="border border-l-0 border-[var(--border)] px-4 metadata hover:bg-[var(--primary)] hover:text-[var(--inverse)]" data-testid="coupon-apply">{t.coupon.apply}</button>
                </div>
              )}
              {couponErr && <div className="text-[var(--accent)] text-xs mt-1">{couponErr}</div>}
              {coupon?.is_gift_card && (
                <div className="text-[10px] mt-2 metadata text-[var(--text-muted)]">
                  KART BALANSI: {formatPrice(Number(coupon.balance_azn) || 0)} · {coupon.multi_use ? "QALIQ SAXLANILACAQ" : "TƏK İSTİFADƏ"}
                </div>
              )}
            </div>

            <button onClick={placeOrder} disabled={submitting || !epointEnabled} className="btn-primary w-full justify-center mt-6 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="place-order-btn">
              <Lock size={12} /> {submitting ? "..." : `${t.checkout.complete} — ${formatPrice(total)}`}
            </button>
          </div>
        </aside>
      </div>
      </>)}
    </div>
  );
}
